import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock, TableBlock } from '../../core/ast/document.types';

export const assignmentCoverTemplate: CoverTemplate = {
  id: 'tpl-assignment-standard',
  name: 'Standard Assignment Cover',
  category: 'assignment',
  description: 'Standard academic submission sheet optimized for homework assignments, problem sets, and individual coursework.',
  thumbnailBadge: 'Assignment',
  version: 3,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'documentTypeLabel', 'title', 'subtitle',
    'courseCode', 'courseTitle', 'sectionOrGroup',
    'studentName', 'studentId', 'studentEmail',
    'facultyName', 'facultyDesignation',
    'submissionDate', 'semester', 'academicYear',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'ASSIGNMENT',
    title: '',
    subtitle: '',
    courseCode: '',
    courseTitle: '',
    sectionOrGroup: '',
    studentName: '',
    studentId: '',
    studentEmail: '',
    facultyName: '',
    facultyDesignation: '',
    submissionDate: '',
    borderStyle: 'academic_box',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock | TableBlock> = [];

    // 1. Center Logo
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-cover-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 26,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    // 2. University Name (Center Aligned)
    blocks.push({
      id: 'blk-cover-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 16, alignment: 'center', color: '#111827' },
      },
    });

    // 3. Address (Center Aligned)
    blocks.push({
      id: 'blk-cover-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 10, alignment: 'center', color: '#4a5563' },
      },
    });

    // Spacing
    blocks.push({
      id: 'blk-cover-sp1',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 12 } },
    });

    // 4. Assignment Badge
    if (values.documentTypeLabel) {
      blocks.push({
        id: 'blk-cover-badge',
        type: 'text',
        createdAt: now,
        data: {
          text: values.documentTypeLabel,
          formatting: { bold: true, fontSizePt: 13, alignment: 'center', color: '#d97706' },
        },
      });
    }

    // 5. Title & Subtitle
    if (values.title) {
      blocks.push({
        id: 'blk-cover-title',
        type: 'text',
        createdAt: now,
        data: {
          text: values.title,
          formatting: { bold: true, fontSizePt: 20, alignment: 'center', color: '#111827' },
        },
      });
    }

    if (values.subtitle) {
      blocks.push({
        id: 'blk-cover-sub',
        type: 'text',
        createdAt: now,
        data: {
          text: values.subtitle,
          formatting: { italic: true, fontSizePt: 12, alignment: 'center', color: '#4b5563' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp2',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 6. Course Meta
    const courseCodeStr = values.courseCode ? `Course: ${values.courseCode}` : '';
    const courseTitleStr = values.courseTitle ? `— ${values.courseTitle}` : '';
    const secStr = values.sectionOrGroup ? `Section: ${values.sectionOrGroup}` : '';
    const semStr = [values.semester, values.academicYear].filter(Boolean).join(' ');

    const line1 = [courseCodeStr, courseTitleStr].filter(Boolean).join(' ');
    const line2 = [secStr, semStr].filter(Boolean).join(' | ');
    const courseMeta = [line1, line2].filter(Boolean).join('\n');

    if (courseMeta) {
      blocks.push({
        id: 'blk-cover-course',
        type: 'text',
        createdAt: now,
        data: {
          text: courseMeta,
          formatting: { bold: true, fontSizePt: 11, alignment: 'center', color: '#374151' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp3',
      type: 'text',
      createdAt: now,
      data: { text: '\n', formatting: { fontSizePt: 10 } },
    });

    // 7. Student & Teacher Submission Box
    blocks.push({
      id: 'blk-cover-box',
      type: 'table',
      createdAt: now,
      data: {
        rows: 2,
        cols: 2,
        hasHeaderRow: true,
        hasHeaderCol: false,
        borderStyle: 'academic',
        cells: [
          [
            { id: 'c00', content: 'STUDENT INFORMATION', isHeader: true, bold: true, alignment: 'left' },
            { id: 'c01', content: 'COURSE INSTRUCTOR', isHeader: true, bold: true, alignment: 'left' },
          ],
          [
            {
              id: 'c10',
              content: `Name: ${values.studentName || ''}\nStudent ID: ${values.studentId || ''}\nEmail: ${values.studentEmail || ''}`,
              alignment: 'left',
            },
            {
              id: 'c11',
              content: `Instructor: ${values.facultyName || ''}\nDesignation: ${values.facultyDesignation || ''}\nDepartment: ${values.facultyDepartment || values.departmentName || ''}`,
              alignment: 'left',
            },
          ],
        ],
      },
    });

    // 8. Date
    if (values.submissionDate) {
      blocks.push({
        id: 'blk-cover-date',
        type: 'text',
        createdAt: now,
        data: {
          text: `\nDate of Submission: ${values.submissionDate}`,
          formatting: { bold: true, fontSizePt: 10.5, alignment: 'center', color: '#6b7280' },
        },
      });
    }

    const coverSection: DocumentSection = {
      id: 'sec-cover-assignment',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
